/**
 * The Assist mark (design/assets/logo; TOKENS.md "The Assist mark").
 *
 * Two roofs of equal weight on one ground line: the seller's on the left, the
 * buyer's on the right. Always decorative here: it sits next to the visible
 * words "Afriqahome Assist", so it is `aria-hidden` and has no title. Never add
 * a tick, shield or stamp to it.
 *
 * Variants follow the asset files: `default` (light grounds), `onMoss`,
 * `onGreen` (forest grounds), `mono` (one colour) and `small` (below 32 px:
 * the two roofs without the ground line).
 */
export type AssistMarkVariant = 'default' | 'onMoss' | 'onGreen' | 'mono' | 'small';

interface MarkColours {
  seller: string;
  yours: string;
  ground: string;
}

const COLOURS: Record<Exclude<AssistMarkVariant, 'small'>, MarkColours> = {
  default: { seller: '#8a938c', yours: '#134025', ground: '#134025' },
  onMoss: { seller: '#7d897f', yours: '#134025', ground: '#134025' },
  onGreen: { seller: '#ffffff', yours: '#e8f771', ground: '#ffffff' },
  mono: { seller: '#17201b', yours: '#17201b', ground: '#17201b' },
};

export interface AssistMarkProps {
  variant?: AssistMarkVariant;
  /** Rendered height in px; the width follows the artwork's ratio. */
  height?: number;
  className?: string;
}

export default function AssistMark({ variant = 'default', height = 24, className }: AssistMarkProps) {
  if (variant === 'small') {
    // assist-mark-small.svg, viewBox 92 x 37.
    return (
      <svg
        aria-hidden="true"
        focusable="false"
        viewBox="2 29 92 37"
        height={height}
        width={Math.round((height * 92) / 37)}
        className={className}
        data-assist-mark="small"
      >
        <path d="M10 58 31 37l21 21" fill="none" stroke="#8a938c" strokeWidth="11" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M44 58 65 37l21 21" fill="none" stroke="#134025" strokeWidth="11" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  const c = COLOURS[variant];
  // assist-mark*.svg, viewBox 108 x 57.
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="2 24 108 57"
      height={height}
      width={Math.round((height * 108) / 57)}
      className={className}
      data-assist-mark={variant}
    >
      <path d="M8 58 38 30l30 28" fill="none" stroke={c.seller} strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M44 58 74 30l30 28" fill="none" stroke={c.yours} strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="8" y="72" width="96" height="7" rx="3.5" fill={c.ground} />
    </svg>
  );
}
